// What the app says about an item whose audio file is no longer there.
//
// Recordings are kept until somebody deletes them — a recording, an assessment,
// a learner or the account (Tan, 2026-10-29). There is no age at which a
// recording goes by itself, so the app promises no retention period anywhere.
//
// This is the fallback for the one case where the row survives its audio: the
// server clears `audio` and dates it on the attempt (`audio_expired_at`),
// keeping the transcript, the per-sound marks and the score. An item nobody
// recorded and an item whose file has gone both arrive as `audio` = null, and
// only that date tells them apart, so the player reads the date rather than
// failing to load something that is not there.
//
// No date means nothing to say: the screens then behave exactly as they always
// have, which is also what every server without that field does.

import moment from 'moment';

// Said where a player would otherwise be. Not "expired": nothing expires.
export const RECORDING_UNAVAILABLE_TITLE = 'Recording unavailable';

/** The day this item's audio file stopped being in storage, or null. */
export const recordingRemovedOn = (item) => {
    const at = item?.audio_expired_at;
    if (!at) return null;

    const date = moment(at);

    return date.isValid() ? date.format('D MMM YYYY') : null;
};

/**
 * True when there is no recording to play and we know its file has gone.
 * False for an item nobody recorded, which has no date on it.
 */
export const recordingIsUnavailable = (item) => !item?.audio && !!recordingRemovedOn(item);

/** The one-line explanation to show instead of a player, or null. */
export const recordingUnavailableDetail = (item) => {
    if (!recordingIsUnavailable(item)) return null;

    return `The audio for this item is no longer in storage (removed on ${recordingRemovedOn(item)}). The transcript, the sound marks and the score are still here.`;
};
